/**
 * Sahibs Delivery — centralized module
 * Single source of truth for delivery date/slot selection, slot closing-time
 * logic, Add-on visibility rules, and the Add-to-Cart gate.
 * Weekend combo validation is handled at click level in theme.liquid.
 */
(function (window, document) {
  'use strict';

  // Capture native fetch immediately before any overrides
  var _nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  var STORAGE_KEY = 'sahibsDeliverySelection';
  var CLOSING_OFFSET_HOURS = 4;
  var ADDON_CUTOFF_HOUR = 16;
  var ADDON_COLLECTION_HANDLE = 'add-on';
  var ADDON_CATEGORY_ID = 'plp-420553457881';
  var ADDON_CATEGORY_LINK = 'a[href="#plp-420553457881"]';
  var DATE_MIN_OFFSET_DAYS = 0;
  var DATE_MAX_OFFSET_DAYS = 30;
  var CART_ADD_URL_PATTERN = /\/cart\/add(\.js)?(\?|$)/;

  // ---------- Slot generation ----------

  function formatHourLabel(hour24) {
    var h = ((hour24 % 24) + 24) % 24;
    var period = h >= 12 ? 'P.M' : 'A.M';
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return h12 + ':00 ' + period;
  }

  function buildSlots() {
    var slots = [];
    for (var start = 12; start <= 22; start++) {
      var end = (start + 1) % 24;
      var startLabel = formatHourLabel(start);
      var endLabel = formatHourLabel(end);
      slots.push({
        startHour: start,
        endHour: end,
        value: startLabel + ' - ' + endLabel,
        label: startLabel + ' - ' + endLabel,
        closingHour: start - CLOSING_OFFSET_HOURS
      });
    }
    return slots;
  }

  var SLOTS = buildSlots();

  // ---------- Manual slot closures (admin-editable) ----------
  // Read from the hidden JSON config sections/delivery-slot-manager.liquid
  // renders into the page (id="sahibs-delivery-admin-config") — an admin
  // edits closed dates/slots and the popup text from Online Store > Themes
  // > Customize, no code edits needed. `date` matches the DD/MM/YYYY format
  // used by the delivery date picker. startHour/endHour define a half-open
  // range [startHour, endHour) matched against each slot's startHour (24h
  // clock) — e.g. 19-23 closes the 7-8PM, 8-9PM, 9-10PM and 10-11PM slots;
  // 0-24 closes the entire day.
  function loadAdminConfig() {
    var fallback = { popupTitle: null, popupMessage: null, closedRanges: [] };
    try {
      var el = document.getElementById('sahibs-delivery-admin-config');
      if (!el) return fallback;
      var parsed = JSON.parse(el.textContent || '{}');
      return {
        popupTitle: parsed.popupTitle || null,
        popupMessage: parsed.popupMessage || null,
        closedRanges: Array.isArray(parsed.closedRanges) ? parsed.closedRanges : []
      };
    } catch (e) {
      console.error('Sahibs Delivery: failed to parse admin config', e);
      return fallback;
    }
  }

  var ADMIN_CONFIG = loadAdminConfig();

  var CLOSED_SLOT_RANGES = ADMIN_CONFIG.closedRanges
    .map(function (r) {
      return {
        date: String(r.date || '').trim(),
        startHour: parseInt(r.startHour, 10),
        endHour: parseInt(r.endHour, 10)
      };
    })
    .filter(function (r) {
      return r.date && !isNaN(r.startHour) && !isNaN(r.endHour);
    });

  function isSlotClosedByOverride(dateStr, startHour) {
    for (var i = 0; i < CLOSED_SLOT_RANGES.length; i++) {
      var r = CLOSED_SLOT_RANGES[i];
      if (r.date === dateStr && startHour >= r.startHour && startHour < r.endHour) {
        return true;
      }
    }
    return false;
  }

  // ---------- Late night slot identifier ----------

  var LATE_NIGHT_SLOT_VALUE = '11:00 P.M - 12:00 A.M';

  function isLateNightSlot(slotValue) {
    return slotValue === LATE_NIGHT_SLOT_VALUE;
  }

  // ---------- Weekend combo ----------

  var WEEKEND_PRODUCT_TYPE = 'weekend special';
  var WEEKEND_DAYS = [0, 5, 6]; // Sunday=0, Friday=5, Saturday=6

  function isWeekendDate(dateStr) {
    var d = parseDMY(dateStr);
    if (!d) return false;
    return WEEKEND_DAYS.indexOf(d.getDay()) !== -1;
  }

  // ---------- November combo (6–9 Nov 2026) ----------
  // All products in the weekend-special-combo collection are restricted to
  // these four specific dates for the November special event. If a customer
  // adds one of these products for a Nov 6-9 date and then changes their
  // delivery date, the combo items are removed from the cart automatically.

  var NOV_COMBO_ALLOWED_DATES = ['06/11/2026', '07/11/2026', '08/11/2026', '09/11/2026'];

  function isNovComboDate(dateStr) {
    return NOV_COMBO_ALLOWED_DATES.indexOf(dateStr) !== -1;
  }

  // ---------- Karivirunthu one-day combo ----------
  // One specific product inside the weekend-special-combo collection
  // (id 10342333743321, "Karivirunthu - Serve 2") that's gated more tightly
  // than the general Fri/Sat/Sun weekend rule above: it's only orderable for
  // a single calendar date and two specific slots. Scoped by product id (not
  // the whole collection) so other weekend-special-combo products, if any
  // are added later, keep following the general weekend rule instead.

  var KARIVIRUNTHU_PRODUCT_ID = '10342333743321';
  var KARIVIRUNTHU_ALLOWED_DATES = ['24/10/2026', '25/10/2026']; // Sat, Sun
  var KARIVIRUNTHU_ALLOWED_SLOTS = ['12:00 P.M - 1:00 P.M', '1:00 P.M - 2:00 P.M'];
  // Bookings close the night before: 24 Oct 2026, 11:00 PM (customer's local
  // device clock, same convention as the rest of this file's date logic).
  var KARIVIRUNTHU_BOOKING_CUTOFF = new Date(2026, 9, 24, 23, 0, 0);

  function isKarivirunthuProduct(productId) {
    return String(productId) === KARIVIRUNTHU_PRODUCT_ID;
  }

  function isKarivirunthuBookingOpen() {
    return new Date() < KARIVIRUNTHU_BOOKING_CUTOFF;
  }

  function isKarivirunthuDateTimeValid(dateStr, slotValue) {
    return KARIVIRUNTHU_ALLOWED_DATES.indexOf(dateStr) !== -1 &&
      KARIVIRUNTHU_ALLOWED_SLOTS.indexOf(slotValue) !== -1;
  }

  // ---------- Recurring weekday late-slot closure ----------
  // Standing weekly rule (not a one-off date, unlike CLOSED_SLOT_RANGES /
  // the admin panel above): the 10PM-11PM and 11PM-12AM slots are
  // permanently closed Monday-Thursday, every week, for every date the
  // date picker allows (today through +DATE_MAX_OFFSET_DAYS). Open
  // normally Friday, Saturday and Sunday.

  var WEEKDAY_LATE_CLOSE_DAYS = [1, 2, 3, 4]; // Date#getDay(): Sun=0 ... Mon=1..Thu=4 ... Sat=6
  var WEEKDAY_LATE_CLOSE_START_HOUR = 22; // 10 PM - covers the 10-11PM and 11PM-12AM slots

  function isSlotClosedByWeekdayRule(dateStr, startHour) {
    if (startHour < WEEKDAY_LATE_CLOSE_START_HOUR) return false;
    var d = parseDMY(dateStr);
    if (!d) return false;
    return WEEKDAY_LATE_CLOSE_DAYS.indexOf(d.getDay()) !== -1;
  }

  // ---------- Date helpers ----------

  function parseDMY(dateStr) {
    if (!dateStr) return null;
    var parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    var d = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    var y = parseInt(parts[2], 10);
    if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
    if (y < 100) y += 2000;
    return new Date(y, m - 1, d);
  }

  function isSameCalendarDate(a, b) {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  function isToday(dateStr) {
    var d = parseDMY(dateStr);
    if (!d) return false;
    return isSameCalendarDate(d, new Date());
  }

  // ---------- Storage ----------

  function getStoredSelection() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function hasSelection() {
    var s = getStoredSelection();
    return !!(s && s.date && s.slot);
  }

  function syncCartAttributes(date, slot) {
    if (!_nativeFetch) return Promise.resolve();
    return _nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { date: date, Time_Slot: slot }
      })
    }).catch(function (err) {
      console.error('Sahibs Delivery: failed to sync cart attributes', err);
    });
  }

  function setSelection(date, slot) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ date: date, slot: slot }));
    } catch (e) {
      console.error('Sahibs Delivery: failed to write sessionStorage', e);
    }
    document.dispatchEvent(new CustomEvent('sahibs:delivery-changed', {
      detail: { date: date, slot: slot }
    }));
  }

  function clearSelection() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('sahibs:delivery-changed', { detail: null }));
  }

  // ---------- Slot availability ----------

  function getSlotsForDate(dateStr) {
    var today = isToday(dateStr);
    var now = new Date();
    return SLOTS.map(function (slot) {
      var disabled = false;
      if (today) {
        var closingTime = new Date(
          now.getFullYear(), now.getMonth(), now.getDate(),
          slot.closingHour, 0, 0
        );
        if (now >= closingTime) disabled = true;
      }
      if (!disabled && isSlotClosedByOverride(dateStr, slot.startHour)) {
        disabled = true;
      }
      if (!disabled && isSlotClosedByWeekdayRule(dateStr, slot.startHour)) {
        disabled = true;
      }
      return {
        startHour: slot.startHour,
        endHour: slot.endHour,
        value: slot.value,
        label: slot.label,
        closingHour: slot.closingHour,
        disabled: disabled
      };
    });
  }

  function isSlotValid(dateStr, slotValue) {
    var slots = getSlotsForDate(dateStr);
    for (var i = 0; i < slots.length; i++) {
      if (slots[i].value === slotValue) {
        return !slots[i].disabled;
      }
    }
    return false;
  }

  // ---------- Add-on visibility (theme pages) ----------

  function shouldHideAddons(dateStr) {
    if (!dateStr) return false;
    if (!isToday(dateStr)) return false;
    return new Date().getHours() >= ADDON_CUTOFF_HOUR;
  }

  function applyAddonVisibility(dateStr) {
    var hide = shouldHideAddons(dateStr);
    document.querySelectorAll('[data-sahibs-addon]').forEach(function (el) {
      el.style.display = hide ? 'none' : '';
    });
    document.dispatchEvent(new CustomEvent('sahibs:addon-visibility-changed', {
      detail: { hidden: hide }
    }));
  }

  // ---------- Add-on visibility (Product List Pro: /apps/menu) ----------

  function updateAddonMenuVisibility(dateStr) {
    var hide = shouldHideAddons(dateStr);

    var category = document.getElementById(ADDON_CATEGORY_ID);
    if (category) category.style.display = hide ? 'none' : '';

    var menuLink = document.querySelector(ADDON_CATEGORY_LINK);
    if (menuLink) {
      var wrapper =
        menuLink.closest('li') ||
        menuLink.closest('.pp-category-nav-item') ||
        menuLink.parentElement;
      if (wrapper) {
        wrapper.style.display = hide ? 'none' : '';
      } else {
        menuLink.style.display = hide ? 'none' : '';
      }
    }
  }

  var addonObserverStarted = false;

  function observeAddonCategoryAppearance() {
    if (window.location.pathname.indexOf('/apps/menu') === -1) return;
    if (addonObserverStarted) return;
    addonObserverStarted = true;

    var throttleTimer = null;
    var observer = new MutationObserver(function () {
      if (throttleTimer) return;
      throttleTimer = setTimeout(function () {
        throttleTimer = null;
        var stored = getStoredSelection();
        updateAddonMenuVisibility(stored ? stored.date : null);
      }, 100);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---------- Add to Cart gate ----------

  function requireSelection(onConfirm) {
    if (hasSelection()) {
      onConfirm(getStoredSelection());
      return;
    }
    if (window.SahibsDeliveryPopup && typeof window.SahibsDeliveryPopup.open === 'function') {
      window.SahibsDeliveryPopup.open({
        message: 'Please select your preferred delivery date and time slot before adding items to your cart.',
        allowSkip: false,
        onConfirm: function (selection) { onConfirm(selection); }
      });
    } else {
      console.error('Sahibs Delivery: popup module not loaded');
    }
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function getRequestMethod(input, init) {
    if (init && init.method) return init.method;
    if (input && typeof input !== 'string' && input.method) return input.method;
    return 'GET';
  }

  function initAddToCartGate() {
    if (!_nativeFetch) return;

    window.fetch = function (input, init) {
      var url = getRequestUrl(input);
      var method = getRequestMethod(input, init);
      var isCartAdd = CART_ADD_URL_PATTERN.test(url) &&
                      String(method).toUpperCase() === 'POST';

      if (!isCartAdd) return _nativeFetch(input, init);

      if (!hasSelection()) {
        return new Promise(function (resolve, reject) {
          requireSelection(function () {
            var sel = getStoredSelection();
            syncCartAttributes(sel.date, sel.slot).then(function () {
              _nativeFetch(input, init).then(resolve, reject);
            });
          });
        });
      }

      var sel = getStoredSelection();
      return syncCartAttributes(sel.date, sel.slot).then(function () {
        return _nativeFetch(input, init);
      });
    };

    // Form-level backup
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;

      var action = form.getAttribute('action') || '';
      var isProductForm = form.getAttribute('is') === 'hdt-product-form';
      var isCartAddAction = action.indexOf('/cart/add') !== -1;

      if (!isProductForm && !isCartAddAction) return;
      if (hasSelection()) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      requireSelection(function () {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }, true);
  }

  // ---------- Init ----------

  function init() {
    initAddToCartGate();

    document.addEventListener('sahibs:delivery-changed', function (e) {
      var date = e.detail ? e.detail.date : null;
      applyAddonVisibility(date);
      updateAddonMenuVisibility(date);
    });

    var existing = getStoredSelection();
    applyAddonVisibility(existing ? existing.date : null);
    updateAddonMenuVisibility(existing ? existing.date : null);
    observeAddonCategoryAppearance();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------- Public API ----------

  window.SahibsDelivery = {
    SLOTS: SLOTS,
    STORAGE_KEY: STORAGE_KEY,
    popupTitle: ADMIN_CONFIG.popupTitle,
    popupMessage: ADMIN_CONFIG.popupMessage,
    ADDON_COLLECTION_HANDLE: ADDON_COLLECTION_HANDLE,
    DATE_MIN_OFFSET_DAYS: DATE_MIN_OFFSET_DAYS,
    DATE_MAX_OFFSET_DAYS: DATE_MAX_OFFSET_DAYS,
    LATE_NIGHT_SLOT_VALUE: LATE_NIGHT_SLOT_VALUE,
    WEEKEND_PRODUCT_TYPE: WEEKEND_PRODUCT_TYPE,
    WEEKEND_DAYS: WEEKEND_DAYS,
    getStoredSelection: getStoredSelection,
    hasSelection: hasSelection,
    setSelection: setSelection,
    clearSelection: clearSelection,
    getSlotsForDate: getSlotsForDate,
    isSlotValid: isSlotValid,
    shouldHideAddons: shouldHideAddons,
    applyAddonVisibility: applyAddonVisibility,
    updateAddonMenuVisibility: updateAddonMenuVisibility,
    requireSelection: requireSelection,
    isToday: isToday,
    parseDMY: parseDMY,
    isLateNightSlot: isLateNightSlot,
    isWeekendDate: isWeekendDate,
    KARIVIRUNTHU_ALLOWED_DATES: KARIVIRUNTHU_ALLOWED_DATES,
    KARIVIRUNTHU_ALLOWED_SLOTS: KARIVIRUNTHU_ALLOWED_SLOTS,
    isKarivirunthuProduct: isKarivirunthuProduct,
    isKarivirunthuBookingOpen: isKarivirunthuBookingOpen,
    isKarivirunthuDateTimeValid: isKarivirunthuDateTimeValid,
    NOV_COMBO_ALLOWED_DATES: NOV_COMBO_ALLOWED_DATES,
    isNovComboDate: isNovComboDate
  };

})(window, document);